import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Activity, Mail, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { BASE_URL } from './config';
import { saveAuth, isAuthenticated } from './utils/auth';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Don't auto-redirect - let user stay on login page
  // This prevents the issue where old tokens cause unwanted redirects
  useEffect(() => {
    // Clear any stale auth data when explicitly visiting login page
    // This ensures a fresh login experience
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('logout') === 'true') {
      localStorage.clear();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await axios.post(`${BASE_URL}/api/superadmin/login/`, {
        email,
        password,
      });

      const token = res.data.access;
      const role = res.data.role;
      const hospitalId = res.data.hospitalId;
      const clinicId = res.data.clinicId;
      const userId = res.data.userId;
      
      // Save to localStorage
      saveAuth(token, role, hospitalId, clinicId, userId);
      
      setError('');
      // Use replace to prevent going back to login page
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('❌ Login error:', err);
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center mb-3">
            <img src="/logo-footer.png" alt="ClinicalAssist" className="h-20" />
          </div>
          {/* <h1 className="text-2xl font-semibold text-slate-800">ClinicalAssist</h1> */}
          <p className="text-sm text-slate-600 mt-1">Healthcare Management System</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-5">Welcome Back</h2>

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email Input */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className="w-full pl-9 pr-10 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-600 hover:text-slate-800 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded transition-colors disabled:opacity-50 text-sm"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-5 text-center">
            <p className="text-slate-600 text-xs">
              Don't have an account?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Register
              </button>
            </p>
          </div>

          {/* Demo Accounts */}
          <div className="mt-5 pt-5 border-t border-slate-200">
            <p className="text-xs text-slate-500 mb-2">Demo Accounts:</p>
            <div className="space-y-1 text-xs text-slate-600">
              <p>• pixeltre@gmail.com</p>
              <p>• bala@gmail.com</p>
              <p>• bala123@gmail.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}